import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, RotateCcw, GraduationCap, Users, Keyboard, Hash, PartyPopper, ShieldCheck, XCircle } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const LOGO = "/mu-logo.png";
const CONTAINER_ID = "scan-app-reader";
const RESULT_TIMEOUT_MS = 2500;
const UNLOCK_TAPS = 5;
const UNLOCK_TAP_WINDOW_MS = 3000;

const MODE_LABEL = { student: "Convocation Check-in", guest: "Convocation Check-in", party: "Night Party Check-in" };
const MODE_META = {
  student: { label: "Student", icon: GraduationCap },
  guest: { label: "Guest", icon: Users },
  party: { label: "Night Party", icon: PartyPopper },
};
const MODE_STORAGE_KEY = "scanAppMode";
const VALID_MODES = ["student", "guest", "party"];

function loadStoredMode() {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return VALID_MODES.includes(stored) ? stored : "student";
  } catch (_e) {
    return "student";
  }
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

const ENDPOINT_BY_MODE = {
  student: "/checkin/student",
  guest: "/checkin/guest",
  party: "/checkin/party",
};
const EXPECTED_QR_TYPE_BY_MODE = { student: "STUDENT", guest: "GUEST" };

export default function ScanApp() {
  const [mode, setModeState] = useState(loadStoredMode); // "student" | "guest" | "party"
  const setMode = (next) => {
    setModeState(next);
    try { window.localStorage.setItem(MODE_STORAGE_KEY, next); } catch (_e) { /* ignore */ }
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [stagedMode, setStagedMode] = useState(mode);
  const stagedModeRef = useRef(mode);
  const [guestPicker, setGuestPicker] = useState(null); // { student, guests } awaiting guest selection
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);

  const selectStagedMode = (key) => {
    stagedModeRef.current = key;
    setStagedMode(key);
  };
  const [pending, setPending] = useState(null); // lookup result awaiting confirm
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // { kind: "success" | "already" | "error", ... } - auto-dismisses
  const [manualMode, setManualMode] = useState(null); // null | "qr" | "htno"
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const startingRef = useRef(false);
  const cooldownRef = useRef(0);
  const resultTimerRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const pausedRef = useRef(false);

  const handleLabelTap = () => {
    tapCountRef.current += 1;
    clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= UNLOCK_TAPS) {
      tapCountRef.current = 0;
      stagedModeRef.current = mode;
      setStagedMode(mode);
      setPickerOpen(true);
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, UNLOCK_TAP_WINDOW_MS);
  };

  const confirmModeChange = () => {
    const chosen = stagedModeRef.current;
    setMode(chosen);
    if (manualMode === "htno" && chosen !== "student") setManualMode(null);
    setPickerOpen(false);
  };

  const cancelModeChange = () => setPickerOpen(false);

  const showResult = (payload) => {
    clearTimeout(resultTimerRef.current);
    setResult(payload);
    resultTimerRef.current = setTimeout(() => setResult(null), RESULT_TIMEOUT_MS);
  };

  const pauseCamera = () => {
    const html5 = scannerRef.current;
    if (!html5 || pausedRef.current) return;
    try {
      const state = html5.getState ? html5.getState() : null;
      if (state === 2) { html5.pause(true); pausedRef.current = true; }
    } catch (_e) { /* ignore */ }
  };

  const resumeCamera = () => {
    const html5 = scannerRef.current;
    if (!html5 || !pausedRef.current) return;
    try {
      html5.resume();
      pausedRef.current = false;
    } catch (_e) { /* ignore */ }
  };

  const lookup = async (qrUuid) => {
    if (!qrUuid) return;
    try {
      const r = await api.get("/checkin/lookup", { params: { qr_uuid: qrUuid.trim() } });
      const currentMode = modeRef.current;
      const expected = EXPECTED_QR_TYPE_BY_MODE[currentMode];
      if (expected && r.data.qr?.qr_type !== expected) {
        showResult({ kind: "error", message: `This is not a ${expected === "STUDENT" ? "student" : "guest"} QR.` });
        return;
      }
      pauseCamera();
      setPending(r.data);
    } catch (e) {
      showResult({ kind: "error", message: apiErrorMessage(e) });
    }
  };

  const cancelPending = () => {
    setPending(null);
    resumeCamera();
  };

  const confirmCheckin = async () => {
    if (!pending) return;
    const endpoint = ENDPOINT_BY_MODE[modeRef.current];
    setConfirming(true);
    try {
      const r = await api.post(endpoint, { qr_uuid: pending.qr.qr_uuid });
      setPending(null);
      resumeCamera();
      showResult({ kind: r.data.status === "already_checked_in" ? "already" : "success", data: r.data });
    } catch (e) {
      setPending({ ...pending, confirmError: apiErrorMessage(e) });
    } finally {
      setConfirming(false);
    }
  };

  const lookupByHtno = async (rollNo) => {
    if (!rollNo) return;
    try {
      const r = await api.get("/checkin/lookup/by-htno", { params: { htno: rollNo.trim() } });
      pauseCamera();
      setPending(r.data);
    } catch (e) {
      showResult({ kind: "error", message: apiErrorMessage(e) });
    }
  };

  const lookupGuestsByHtno = async (rollNo) => {
    if (!rollNo) return;
    try {
      const r = await api.get("/checkin/lookup/guests-by-htno", { params: { htno: rollNo.trim() } });
      pauseCamera();
      setGuestPicker(r.data);
    } catch (e) {
      showResult({ kind: "error", message: apiErrorMessage(e) });
    }
  };

  const pickGuest = (qrUuid) => {
    setGuestPicker(null);
    lookup(qrUuid);
  };

  const cancelGuestPicker = () => {
    setGuestPicker(null);
    resumeCamera();
  };

  const submitManual = () => {
    if (!manual.trim()) return;
    if (manualMode === "htno") {
      if (modeRef.current === "guest") lookupGuestsByHtno(manual);
      else lookupByHtno(manual);
    } else {
      lookup(manual);
    }
    setManual(""); setManualMode(null);
  };

  const start = async () => {
    if (startingRef.current || scannerRef.current) return;
    startingRef.current = true;
    try {
      const html5 = new Html5Qrcode(CONTAINER_ID, false);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (vw, vh) => ({ width: Math.round(Math.min(vw, vh) * 0.7), height: Math.round(Math.min(vw, vh) * 0.7) }) },
        (decoded) => {
          const now = Date.now();
          if (pausedRef.current || now - cooldownRef.current < RESULT_TIMEOUT_MS) return;
          cooldownRef.current = now;
          lookup(decoded);
        },
        () => {}
      );
    } catch (e) {
      showResult({ kind: "error", message: "Camera unavailable: " + e.message });
      scannerRef.current = null;
    } finally {
      startingRef.current = false;
    }
  };

  const stop = async () => {
    const html5 = scannerRef.current;
    scannerRef.current = null;
    pausedRef.current = false;
    if (!html5) return;
    try {
      const state = html5.getState ? html5.getState() : null;
      if (state === 2 || state === 3) await html5.stop();
      await html5.clear();
    } catch (_e) { /* ignore - element may already be gone on unmount */ }
  };

  useEffect(() => {
    start();
    return () => { clearTimeout(resultTimerRef.current); clearTimeout(tapTimerRef.current); stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partyBlocked = mode === "party" && pending && !pending.convocation_checked_in_at;
  const alreadyThisType = pending && (
    mode === "party" ? pending.party_checked_in_at : pending.convocation_checked_in_at
  );

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col" data-testid="scan-app">
      <div className="flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
        <img src={LOGO} alt="MU" className="h-8 w-auto invert brightness-0" />
        <button
          type="button"
          data-testid="scan-mode-label"
          onClick={handleLabelTap}
          className="flex flex-col items-end gap-1.5 select-none"
        >
          <span className="text-xs tracking-[0.25em] uppercase text-white/50">
            {MODE_LABEL[mode]}
          </span>
          <span
            data-testid="scan-mode-badge"
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-white bg-white/10 px-2.5 py-1 rounded-full"
          >
            {(() => { const Icon = MODE_META[mode].icon; return <Icon size={11} />; })()}
            {MODE_META[mode].label}
          </span>
        </button>
      </div>

      <div className="relative flex-1 mx-5 mb-5 rounded-2xl overflow-hidden bg-slate-900">
        {/* This div is owned exclusively by html5-qrcode - never render React children into it. */}
        <div id={CONTAINER_ID} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />

        {/* Decorative corner-bracket viewfinder, sized to match the real
            (square, 70%-of-min-dimension) qrbox region — html5-qrcode's own
            internal viewfinder border is square-cornered and not easily
            restyled. w-[70%] + aspect-square + max-h-[70%] together give
            min(70% width, 70% height) of the container, purely via CSS. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-[70%] max-h-[70%] aspect-square">
            <span className="absolute top-0 left-0 w-10 h-10 border-t-[4px] border-l-[4px] border-white/90 rounded-tl-2xl" />
            <span className="absolute top-0 right-0 w-10 h-10 border-t-[4px] border-r-[4px] border-white/90 rounded-tr-2xl" />
            <span className="absolute bottom-0 left-0 w-10 h-10 border-b-[4px] border-l-[4px] border-white/90 rounded-bl-2xl" />
            <span className="absolute bottom-0 right-0 w-10 h-10 border-b-[4px] border-r-[4px] border-white/90 rounded-br-2xl" />
          </div>
        </div>

        {!result && !pending && (
          <div className="absolute inset-x-0 bottom-4 flex justify-center pointer-events-none">
            <p className="text-xs tracking-[0.2em] uppercase text-white/50 bg-black/40 px-3 py-1.5 rounded-full">
              Point camera at QR code
            </p>
          </div>
        )}

        {pending && (
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center px-6 text-center animate-in fade-in duration-200" data-testid="scan-verify">
            <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-4">Verify Before Check-in</p>
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center font-serif text-2xl font-semibold mb-3">
              {initials(pending.student.student_name)}
            </div>
            <h2 className="font-serif text-3xl font-extrabold">{pending.student.student_name}</h2>
            <p className="text-white/60 mt-1">{pending.student.htno}</p>
            {pending.qr?.qr_type === "GUEST" && (
              <p className="text-white/70 text-sm mt-3">
                Guest: {pending.qr.guest_name || "—"} ({pending.qr.guest_relation || "—"})
              </p>
            )}
            <p className="text-white/40 text-xs mt-2">{pending.student.entity} · {pending.student.program}</p>

            {pending.qr?.qr_type === "STUDENT" && pending.sash && (
              <div
                className="flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: pending.sash.color }}
                data-testid="sash-badge"
              >
                {pending.sash.label}
              </div>
            )}

            {mode === "party" && (
              <div className={`mt-4 px-4 py-2 rounded-full text-xs font-medium ${pending.convocation_checked_in_at ? "bg-emerald-500/20 text-emerald-300" : "bg-primary/30 text-red-200"}`}>
                {pending.convocation_checked_in_at
                  ? "Attended convocation ✓"
                  : "NOT checked in to convocation — blocked"}
              </div>
            )}
            {alreadyThisType && (
              <div className="mt-2 px-4 py-2 rounded-full text-xs bg-amber-500/20 text-amber-300">
                Already checked in at {new Date(alreadyThisType).toLocaleTimeString()}
              </div>
            )}
            {pending.confirmError && (
              <p className="text-red-300 text-sm mt-3">{pending.confirmError}</p>
            )}

            <div className="flex gap-3 mt-8 w-full max-w-xs">
              <Button
                data-testid="scan-cancel-btn"
                variant="outline"
                className="flex-1 h-12 bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={cancelPending}
              >
                <XCircle size={16} className="mr-1" /> Cancel
              </Button>
              <Button
                data-testid="scan-confirm-btn"
                className="flex-1 h-12 bg-primary hover:bg-primary/90 disabled:opacity-40"
                disabled={confirming || partyBlocked}
                onClick={confirmCheckin}
              >
                <ShieldCheck size={16} className="mr-1" /> Confirm
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center px-6 text-center" data-testid="scan-result">
            {result.kind === "error" ? (
              <>
                <AlertTriangle size={48} className="text-primary mb-4" />
                <p className="text-lg font-semibold">{result.message}</p>
              </>
            ) : (
              <>
                {result.kind === "success" ? (
                  <CheckCircle2 size={56} className="text-emerald-400 mb-4" />
                ) : (
                  <RotateCcw size={56} className="text-amber-400 mb-4" />
                )}
                <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-2">
                  {result.kind === "success" ? "Checked In" : "Already Checked In"}
                </p>
                <h2 className="font-serif text-3xl font-extrabold">{result.data.student?.student_name}</h2>
                <p className="text-white/60 mt-1">{result.data.student?.htno}</p>
                {result.data.qr?.qr_type && result.data.qr.qr_type !== "STUDENT" && (
                  <p className="text-white/40 text-sm mt-3">
                    Guest: {result.data.qr.guest_name || "—"} ({result.data.qr.guest_relation || "—"})
                  </p>
                )}
                {result.data.qr?.qr_type === "STUDENT" && result.data.sash && (
                  <div
                    className="flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: result.data.sash.color }}
                    data-testid="sash-badge-result"
                  >
                    {result.data.sash.label}
                  </div>
                )}
              </>
            )}
            <p className="text-white/30 text-xs mt-8">Resuming scan…</p>
          </div>
        )}
      </div>

      <div className="px-5 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {manualMode ? (
          <div className="flex gap-2">
            <Input
              data-testid="scan-manual-input"
              autoFocus
              placeholder={manualMode === "htno" ? "e.g. SE24MAEV005" : "Paste QR UUID"}
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11"
            />
            <Button
              data-testid="scan-manual-submit"
              className="bg-primary hover:bg-primary/90 shrink-0 h-11"
              onClick={submitManual}
            >
              Look Up
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-6">
            <button
              data-testid="scan-manual-toggle"
              onClick={() => setManualMode("qr")}
              className="flex items-center gap-2 py-2.5 text-sm text-white/50"
            >
              <Keyboard size={14} /> Enter QR manually
            </button>
            <button
              data-testid="scan-htno-toggle"
              onClick={() => setManualMode("htno")}
              className="flex items-center gap-2 py-2.5 text-sm text-white/50"
            >
              <Hash size={14} /> Enter roll number
            </button>
          </div>
        )}
      </div>

      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center px-6 text-center animate-in fade-in duration-150"
          data-testid="scan-mode-picker"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-6">Select Check-in Mode</p>
          <div className="grid grid-cols-1 gap-3 w-full max-w-xs">
            {[
              { key: "student", label: "Student — Convocation", icon: GraduationCap },
              { key: "guest", label: "Guest — Convocation", icon: Users },
              { key: "party", label: "Night Party", icon: PartyPopper },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                data-testid={`picker-mode-${key}`}
                onClick={() => selectStagedMode(key)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold border transition-colors ${
                  stagedMode === key
                    ? "bg-primary border-primary text-white"
                    : "bg-white/5 border-white/15 text-white/70 hover:bg-white/10"
                }`}
              >
                <Icon size={18} /> {label}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-8 w-full max-w-xs">
            <Button
              data-testid="picker-cancel-btn"
              variant="outline"
              className="flex-1 h-12 bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={cancelModeChange}
            >
              <XCircle size={16} className="mr-1" /> Cancel
            </Button>
            <Button
              data-testid="picker-confirm-btn"
              className="flex-1 h-12 bg-primary hover:bg-primary/90"
              onClick={confirmModeChange}
            >
              <ShieldCheck size={16} className="mr-1" /> Confirm
            </Button>
          </div>
        </div>
      )}

      {guestPicker && (
        <div
          className="fixed inset-0 z-40 bg-black/95 flex flex-col items-center justify-center px-6 text-center animate-in fade-in duration-150"
          data-testid="guest-picker"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-2">Select Guest</p>
          <h2 className="font-serif text-2xl font-bold">{guestPicker.student.student_name}</h2>
          <p className="text-white/50 text-sm mt-1 mb-6">{guestPicker.student.htno}</p>

          <div className="grid grid-cols-1 gap-3 w-full max-w-xs max-h-[50vh] overflow-y-auto">
            {guestPicker.guests.map((g) => {
              const alreadyIn = mode === "party" ? g.party_checked_in_at : g.convocation_checked_in_at;
              return (
                <button
                  key={g.qr.id}
                  type="button"
                  data-testid={`guest-picker-option-${g.qr.guest_index}`}
                  onClick={() => pickGuest(g.qr.qr_uuid)}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold border border-white/15 bg-white/5 text-white/80 hover:bg-white/10 transition-colors text-left"
                >
                  <span>
                    Guest {g.qr.guest_index}: {g.qr.guest_name || "—"}
                    <span className="block text-xs font-normal text-white/50">{g.qr.guest_relation || "—"}</span>
                  </span>
                  {alreadyIn && (
                    <span className="text-[10px] uppercase tracking-wide bg-amber-500/20 text-amber-300 px-2 py-1 rounded-full shrink-0">
                      Checked in
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <Button
            data-testid="guest-picker-cancel-btn"
            variant="outline"
            className="mt-8 h-12 w-full max-w-xs bg-white/10 border-white/20 text-white hover:bg-white/20"
            onClick={cancelGuestPicker}
          >
            <XCircle size={16} className="mr-1" /> Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
